import { createFileRoute, Link } from "@tanstack/react-router";
import { CONSULTING_PAGES } from "@/components/ConsultingExpertise";

import { ArrowUpRight, ArrowRight, ShieldCheck, Radar, Cpu, Film, Speaker, Quote } from "lucide-react";
import { motion } from "motion/react";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { WhoIHelp } from "@/components/WhoIHelp";
import { SITE, STATS, SOCIAL } from "@/lib/site";
import { getArticle, getCategory } from "@/lib/askArif";
import { completeFeaturedPosts } from "@/config/linkedin";
import { getPublishedLinkedInCards } from "@/lib/linkedin-cards.functions";
import heroImg from "@/assets/hero.jpg";

// Responsive variants for the LCP hero image. vite-imagetools emits AVIF and
// WebP at multiple widths so mobile downloads ~25 KB instead of the full
// 140 KB JPEG. Addresses PSI "Improve image delivery" (~80 KB savings).
import heroAvifSet from "@/assets/hero.jpg?w=640;960;1280;1920&format=avif&as=srcset";
import heroWebpSet from "@/assets/hero.jpg?w=640;960;1280;1920&format=webp&as=srcset";
import heroMobileWebp from "@/assets/hero.jpg?w=960&format=webp";
// Official primary portrait: responsive AVIF/WebP variants for delivery; the
// <img> fallback uses the stable public URL shared with JSON-LD and og:image.
import portraitAvifSet from "@/assets/arif-khan-primary.jpg?w=480;720;960;1280&format=avif&as=srcset";
import portraitWebpSet from "@/assets/arif-khan-primary.jpg?w=480;720;960;1280&format=webp&as=srcset";
import portraitPreload from "@/assets/arif-khan-primary.jpg?w=720&format=webp";
import portraitLqip from "@/assets/arif-khan-primary.jpg?w=32&blur=24&format=webp";
import { hreflangLinks } from "@/lib/hreflang";
import {
  pageSchemaScript,
  PERSON_ID,
  PORTRAIT_ID,
  ALTIMA_URL,
  PRIMARY_PORTRAIT_PATH,
  PRIMARY_PORTRAIT_URL,
  PRIMARY_PORTRAIT_WIDTH,
  PRIMARY_PORTRAIT_HEIGHT,
  PRIMARY_PORTRAIT_ALT,
} from "@/seo/schema";

const PORTRAIT_OG_ALT = "Arif Khan, Security Infrastructure and Building Technology Strategist at Altima Global.";


// Single title is reused as <title> and og:title to avoid social-share
// mismatch (Phase 0 metadata fix).
const HOME_TITLE = "Arif Khan — Security & Building Technology Strategist";
const HOME_DESC = "Arif Khan — 23+ years designing electronic security, auditorium AV, home automation & luxury cinema solutions across India.";


export const Route = createFileRoute("/")({
  loader: async () => ({ linkedinCards: await getPublishedLinkedInCards() }),
  component: Home,
  head: () => ({
    meta: [
      { title: HOME_TITLE },
      { name: "description", content: HOME_DESC },
      { property: "og:title", content: HOME_TITLE },
      { property: "og:description", content: HOME_DESC },
      { property: "og:url", content: "https://arifkhanglobal.com/" },
      { property: "og:image", content: PRIMARY_PORTRAIT_URL },
      { property: "og:image:width", content: String(PRIMARY_PORTRAIT_WIDTH) },
      { property: "og:image:height", content: String(PRIMARY_PORTRAIT_HEIGHT) },
      { property: "og:image:alt", content: PORTRAIT_OG_ALT },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: HOME_TITLE },
      { name: "twitter:description", content: HOME_DESC },
      { name: "twitter:image", content: PRIMARY_PORTRAIT_URL },
      { name: "twitter:image:alt", content: PORTRAIT_OG_ALT },
    ],
    links: [
      { rel: "canonical", href: "https://arifkhanglobal.com/" },
      ...hreflangLinks("/"),
      { rel: "alternate", type: "text/markdown", href: "https://arifkhanglobal.com/index.md", title: "Markdown mirror" },
      // Preload the mobile-sized WebP since that's what most visitors download.
      // Desktop browsers using <picture> will still pick the right srcset entry.
      { rel: "preload", as: "image", href: heroMobileWebp, fetchPriority: "high", type: "image/webp" },
      // Preload the hero portrait so the above-the-fold image is fetched in parallel with the LCP background.
      { rel: "preload", as: "image", href: portraitPreload, fetchPriority: "high", type: "image/webp" },
    ],
    scripts: [
      pageSchemaScript({
        path: "/",
        type: "WebPage",
        name: HOME_TITLE,
        description: HOME_DESC,
        extra: {
          about: { "@id": PERSON_ID },
          mainEntity: { "@id": PERSON_ID },
          primaryImageOfPage: { "@id": PORTRAIT_ID },
        },
      }),
    ],

  }),
});



// Five high-level expertise pillars — each links to its existing solution page.
const PILLARS = [
  { slug: "security-infrastructure", Icon: ShieldCheck, title: "Security Infrastructure", text: "CCTV, access control and command-centre design planned as one security system rather than separate products.", cta: "Explore Security Infrastructure" },
  { slug: "perimeter-protection", Icon: Radar, title: "Perimeter Protection", text: "Layered detection for large boundaries, from energised fencing to fibre sensing and video verification.", cta: "Explore Perimeter Protection" },
  { slug: "smart-automation", Icon: Cpu, title: "Smart Automation", text: "Lighting, climate, gates and security tied into one control system that stays simple to use.", cta: "Explore Smart Automation" },
  { slug: "luxury-home-cinema", Icon: Film, title: "Luxury Home Cinema", text: "Private theatres where room shape, acoustics and sightlines are resolved before equipment is chosen.", cta: "Explore Luxury Home Cinema" },
  { slug: "auditorium-av", Icon: Speaker, title: "Auditorium & Enterprise AV", text: "Sound, projection and control for auditoriums, boardrooms and training spaces.", cta: "Explore Auditorium AV" },
] as const;

// Three verified landmark engagements already published on the site.
const SELECTED_WORK = [
  {
    type: "Perimeter Security · Defence",
    name: "INS Rajali Naval Air Station",
    challenge: "A high-vegetation coastal boundary where environmental false alarms had to be suppressed.",
    scale: "15 km integrated perimeter",
    solution: { label: "Explore Perimeter Protection", slug: "perimeter-protection" },
  },
  {
    type: "Auditorium AV · Institution",
    name: "International Centre for Performing Arts & Culture, Shillong",
    challenge: "Consistent intelligibility across a large seating bowl, with a control workflow local operators can run.",
    scale: "2,700-seat auditorium",
    solution: { label: "Explore Auditorium AV", slug: "auditorium-av" },
  },
  {
    type: "Luxury Residences · Delhi NCR",
    name: "Residences & farmhouses for HNI clients",
    challenge: "Estate security, automation and private cinema without retrofit costs after interiors are frozen.",
    scale: "Multi-year engagements",
    solution: { label: "Explore Luxury Home Cinema", slug: "luxury-home-cinema" },
  },
] as const;

const ASK_ARIF_PICKS = [
  "how-does-perimeter-security-work",
  "common-mistakes-in-home-theatre-projects",
  "how-much-does-home-automation-cost",
];

const TESTIMONIALS = [
  {
    quote: "Arif brings the rare combination of deep engineering rigor and a strategist's view of risk. He designs systems that disappear into the architecture.",
    name: "Jatin - Principal Architect",
    org: "Pan-India Architecture Practice",
  },
  {
    quote: "Our perimeter program scaled from concept to 50+ kilometres because the framework was right from day one. That clarity is his signature.",
    name: "Piyush - Head of Infrastructure",
    org: "National Logistics Operator",
  },
];

function Home() {
  return (
    <PageShell>
      <Hero />
      <AuthorityStrip />
      <WhoIHelp />
      <ExpertisePillars />
      <ConsultingExpertise />
      <SelectedWork />
      <AskArifPicks />
      <LatestLinkedIn />
      <Testimonials />
      <FinalCTA />
    </PageShell>
  );
}

function Hero() {
  return (
    <section className="relative flex flex-col justify-center pt-32 pb-20 lg:min-h-[92vh] overflow-hidden">
      <div className="absolute inset-0 -z-20 overflow-hidden">
        <picture>
          <source type="image/avif" srcSet={heroAvifSet} sizes="100vw" />
          <source type="image/webp" srcSet={heroWebpSet} sizes="100vw" />
          <img
            src={heroImg}
            alt="Modern smart building at twilight with integrated architectural lighting"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover animate-ken-burns"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/70 to-background" />
        <div className="absolute inset-0 bg-radial-primary opacity-70" />
      </div>
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="container-page relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="text-balance text-5xl md:text-7xl lg:text-[5.5rem] font-semibold tracking-tight leading-[0.95]"
            >
              Arif Khan
            </motion.h1>
            <motion.p
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 text-balance text-2xl md:text-3xl font-medium tracking-tight text-primary leading-tight"
            >
              {SITE.role}
            </motion.p>
            <motion.p
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 max-w-2xl text-pretty text-lg md:text-xl text-ink-soft leading-relaxed"
            >
              I help owners, architects and institutions plan security, automation, AV and
              private cinema systems before costly mistakes reach the site.
            </motion.p>
            <motion.p
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-4 max-w-2xl text-sm md:text-base text-ink-soft leading-relaxed"
            >
              BU Head, Security &amp; Automation,{" "}
              <a href={ALTIMA_URL} target="_blank" rel="noopener" className="font-medium text-foreground underline-offset-2 hover:underline">
                Altima Global
              </a>
              <span className="block">New Delhi · Projects across India</span>
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
              >
                Discuss a Project
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href="#selected-work"
                className="inline-flex items-center gap-2 rounded-full border border-border-strong px-7 py-3.5 text-sm font-medium text-foreground hover:bg-surface transition-colors"
              >
                Explore My Work
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ x: 16 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <figure className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-primary/20 via-transparent to-accent/10 blur-2xl" aria-hidden />
              <div className="relative overflow-hidden rounded-2xl border border-border-strong/60 shadow-[0_30px_80px_-30px_rgba(21,101,192,0.45)] glass aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] bg-surface max-h-[78vh] mx-auto w-full">
                <HeroPortrait />
              </div>
              <figcaption className="sr-only">
                Arif Khan — Security Infrastructure &amp; Building Technology Strategist, BU Head, Security &amp; Automation at Altima Global, New Delhi.
              </figcaption>
            </figure>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// The ONLY place homepage statistics appear.
function AuthorityStrip() {
  return (
    <section aria-label="Experience at a glance" className="border-y border-border bg-surface">
      <dl className="container-page grid grid-cols-3 divide-x divide-border">
        {STATS.map((s) => (
          <div key={s.label} className="py-8 md:py-12 px-3 md:px-10 first:pl-0 text-center md:text-left flex flex-col-reverse">
            <dt className="mt-2 text-[11px] md:text-sm text-ink-soft leading-snug">{s.label}</dt>
            <dd className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground">{s.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ExpertisePillars() {
  return (
    <section className="py-24 md:py-32 bg-surface border-y border-border" aria-labelledby="expertise">
      <div className="container-page">
        <SectionHeader
          eyebrow="Expertise"
          title={<span id="expertise">Five disciplines, planned as part of the building.</span>}
          description="Technology systems work best when they are planned as part of the building, not added after construction decisions are already fixed."
        />
        <div className="mt-14 grid gap-px bg-border border border-border rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.04}>
              <Link
                to="/solutions/$slug"
                params={{ slug: p.slug }}
                className="group flex h-full flex-col bg-background p-7 hover:bg-surface transition-colors"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <p.Icon className="h-4 w-4" strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 text-lg font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-3 flex-1 text-sm text-ink-soft leading-relaxed">{p.text}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                  {p.cta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <nav aria-label="More ways to explore" className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium">
          <Link to="/solutions" className="tap-target inline-flex items-center gap-1.5 text-primary hover:text-foreground">All solutions <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/industries" className="tap-target inline-flex items-center gap-1.5 text-primary hover:text-foreground">Industries served <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/locations" className="tap-target inline-flex items-center gap-1.5 text-primary hover:text-foreground">Locations across India <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/about" className="tap-target inline-flex items-center gap-1.5 text-primary hover:text-foreground">About Arif Khan <ArrowRight className="h-4 w-4" /></Link>
        </nav>
      </div>
    </section>
  );
}

function ConsultingExpertise() {
  return (
    <section className="py-20 md:py-24" aria-labelledby="consulting-expertise">
      <div className="container-page">
        <span className="eyebrow">Consulting Expertise</span>
        <h2 id="consulting-expertise" className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
          Consulting expertise across India
        </h2>
        <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CONSULTING_PAGES.map((p) => (
            <li key={p.to}>
              <Link to={p.to} className="group flex h-full flex-col rounded-2xl border border-border p-6 hover:border-primary transition-colors">
                <h3 className="text-base font-semibold tracking-tight">{p.label}</h3>
                <p className="mt-3 flex-1 text-sm text-ink-soft leading-relaxed">{p.text}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                  Read more <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SelectedWork() {
  return (
    <section id="selected-work" className="py-24 md:py-32 scroll-mt-24" aria-labelledby="selected-work-title">
      <div className="container-page">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeader
            eyebrow="Selected Work"
            title={<span id="selected-work-title">Projects where the planning mattered.</span>}
          />
          <Link to="/projects" className="tap-target inline-flex shrink-0 items-center gap-2 text-sm font-medium text-primary hover:text-foreground transition-colors">
            View Selected Projects <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {SELECTED_WORK.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}>
              <article className="flex h-full flex-col rounded-2xl border border-border bg-background p-8">
                <div className="eyebrow text-accent">{p.type}</div>
                <h3 className="mt-4 text-xl font-semibold leading-snug tracking-tight">{p.name}</h3>
                <p className="mt-3 text-sm font-medium text-primary">{p.scale}</p>
                <p className="mt-4 flex-1 text-base leading-relaxed text-ink-soft">{p.challenge}</p>
                <Link
                  to="/solutions/$slug"
                  params={{ slug: p.solution.slug }}
                  className="tap-target mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-primary"
                >
                  {p.solution.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AskArifPicks() {
  const items = ASK_ARIF_PICKS.map((slug) => getArticle(slug)).filter((a): a is NonNullable<typeof a> => Boolean(a));
  return (
    <section className="py-24 md:py-32 bg-surface border-y border-border" aria-labelledby="ask-arif-title">
      <div className="container-page">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeader
            eyebrow="Ask Arif"
            title={<span id="ask-arif-title">Practical answers from real projects.</span>}
            description="Practical answers from real project experience across security, automation, perimeter protection and AV."
          />
          <Link to="/ask-arif" className="tap-target inline-flex shrink-0 items-center gap-2 text-sm font-medium text-primary hover:text-foreground transition-colors">
            Read Ask Arif <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {items.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.06}>
              <Link
                to="/ask-arif/$slug"
                params={{ slug: a.slug }}
                className="group flex h-full flex-col rounded-2xl border border-border bg-background p-8 hover:border-border-strong transition-colors"
              >
                <span className="eyebrow text-accent">{getCategory(a.category)?.title ?? a.category}</span>
                <h3 className="mt-4 text-xl font-semibold leading-snug tracking-tight">{a.question}</h3>
                <p className="mt-3 flex-1 text-sm text-ink-soft leading-relaxed line-clamp-3">{a.shortAnswer}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                  Read Answer <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/insights" className="tap-target inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-foreground">
            Browse all Insights <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Native cards from src/config/linkedin.ts — no iframe, no feed script.
// Incomplete entries are hidden; with none complete, only the profile link shows.
function LatestLinkedIn() {
  const { linkedinCards } = Route.useLoaderData();
  const posts = linkedinCards.length > 0 ? linkedinCards : completeFeaturedPosts();
  return (
    <section className="py-24 md:py-32" aria-labelledby="linkedin-title">
      <div className="container-page">
        <SectionHeader
          eyebrow="LinkedIn"
          title={<span id="linkedin-title">Latest from LinkedIn</span>}
          description="Current observations on security, automation, AV and building technology from projects and industry conversations."
        />
        {posts.length > 0 && (
          <ul className="mt-14 -mx-6 px-6 flex gap-6 overflow-x-auto snap-x snap-mandatory pb-2 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:overflow-visible">
            {posts.map((p) => (
              <li key={p.linkedinUrl} className="snap-start shrink-0 w-[85%] md:w-auto">
                <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background transition-colors hover:border-border-strong">
                  <img
                    src={p.image}
                    alt={p.imageAlt ?? p.title}
                    width={1200}
                    height={675}
                    loading="lazy"
                    decoding="async"
                    className="aspect-video w-full object-cover bg-surface"
                  />
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center justify-between gap-3 text-xs text-ink-soft">
                      {p.category && <span className="eyebrow text-accent">{p.category}</span>}
                      <time dateTime={p.date}>
                        {new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
                      </time>
                    </div>
                    <h3 className="mt-4 text-lg font-semibold leading-snug tracking-tight">{p.title}</h3>
                    <p className="mt-3 flex-1 text-sm text-ink-soft leading-relaxed">{p.summary}</p>
                    <div className="mt-6 flex flex-col gap-2 text-xs font-semibold">
                      {p.internalUrl && (
                        <a href={p.internalUrl} className="tap-target inline-flex items-center gap-1.5 text-primary">
                          Read the Full Insight <ArrowRight className="h-3.5 w-3.5" />
                        </a>
                      )}
                      <a href={p.linkedinUrl} target="_blank" rel="noopener" className={`tap-target inline-flex items-center gap-1.5 ${p.internalUrl ? "text-ink-soft hover:text-foreground" : "text-primary"}`}>
                        {p.internalUrl ? "View Discussion on LinkedIn" : "View on LinkedIn"} <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-8">
          <a href={SOCIAL.linkedin} target="_blank" rel="noopener me" className="tap-target inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-foreground">
            View Arif Khan's full LinkedIn profile <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-24 md:py-32" aria-labelledby="feedback-title">
      <div className="container-page">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow text-primary">Representative Feedback</span>
          <h2 id="feedback-title" className="mt-4 text-balance text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            How collaborators describe the work.
          </h2>
          <p className="mt-5 text-pretty text-base text-ink-soft leading-relaxed">
            Composite, representative feedback from long-running engagements — paraphrased to protect client confidentiality.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <figure className="rounded-2xl border border-border p-8 md:p-10 bg-background h-full flex flex-col">
                <Quote className="h-6 w-6 text-primary mb-6" strokeWidth={1.5} aria-hidden />
                <blockquote className="text-lg md:text-xl font-medium leading-snug tracking-tight text-foreground">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-8 pt-6 border-t border-border">
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-ink-soft mt-1">{t.org}</div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 md:py-32 bg-surface border-t border-border" aria-labelledby="final-cta">
      <div className="container-page max-w-3xl text-center">
        <h2 id="final-cta" className="text-balance text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
          Planning Security, Automation or AV for a Project?
        </h2>
        <p className="mt-6 text-pretty text-base md:text-lg text-ink-soft leading-relaxed">
          Early technology planning usually costs less than correcting infrastructure after interiors, ceilings or electrical work are complete.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Discuss a Project
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to="/for-architects"
            className="inline-flex items-center gap-2 rounded-full border border-border-strong px-7 py-3.5 text-sm font-medium text-foreground hover:bg-background transition-colors"
          >
            For Architects
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}


// Hero portrait: responsive <picture> with AVIF + WebP srcsets. The wrapper
// carries a tiny blurred LQIP as background plus an animated skeleton so the
// portrait renders smoothly while loading. The decoded image opaquely covers
// both layers — no JS state needed, so SSR/hydration cannot leave a stuck blur.
function HeroPortrait() {
  const alt = PRIMARY_PORTRAIT_ALT;
  const sizes = "(min-width: 1024px) 40vw, (min-width: 640px) 60vw, 90vw";
  return (
    <>
      {/* Skeleton shimmer — covered as soon as the LQIP / image paints. */}
      <div aria-hidden className="absolute inset-0 bg-muted animate-pulse" />
      {/* LQIP blur-up — also covered once the full image decodes. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-center bg-cover scale-110 blur-xl"
        style={{ backgroundImage: `url(${portraitLqip})` }}
      />
      <picture>
        <source type="image/avif" srcSet={portraitAvifSet} sizes={sizes} />
        <source type="image/webp" srcSet={portraitWebpSet} sizes={sizes} />
        <img
          src={PRIMARY_PORTRAIT_PATH}
          alt={alt}
          width={PRIMARY_PORTRAIT_WIDTH}
          height={PRIMARY_PORTRAIT_HEIGHT}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="relative h-full w-full object-cover object-[62%_30%]"
        />
      </picture>
    </>
  );
}

